import { createReference, MedplumClient } from "@medplum/core";
import {
  List,
  Practitioner,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from "@medplum/fhirtypes";

import { format, formatISO } from "date-fns";

import {
  buildEddGroupItemFromDatingSummary,
  mergeCloseoutItemsWithObservationEdd,
} from "@/clients/utils/closed-out-pregnancy-dating";
import { fetchIntakeQuestionnaire } from "@/consents/services/consents";
import { ORGANIZATION_ID } from "@/constants/local-storage";
import { Extensions } from "@/fhir/enums/extensions";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import { Systems } from "@/fhir/enums/systems";
import { buildPregnancyRecord, closePregnancyRecord, fetchPregnancyRecordList } from "@/pregnancy";
import {
  getQuestionnaireResponseItems,
  joinReferenceString,
  setExtensionValue,
} from "@/utils/medplum";

import { PregnancyCloseoutFormValues } from "../zod-schemas/pregnancy-closeout";

import { fetchEDDQuestionnaire, fetchEDDQuestionnaireResponse } from "./edd";
import { fetchGTPALQuestionnaire } from "./gtpal";

export const closeOutPregnancy = async (
  medplum: MedplumClient,
  clientId: string,
  answers: PregnancyCloseoutFormValues,
  identifier: string,
): Promise<void> => {
  const questionnaireResponse = await generateGTPALCloseOutQuestionnaireResponse(
    medplum,
    clientId,
    answers,
    identifier,
  );
  await generateCloseOutQuestionnaireResponse(medplum, clientId);
  await medplum.createResource(questionnaireResponse);
  await closePregnancyRecord(medplum, clientId, {
    closeOutDate: formatISO(answers.closeoutDate),
  });

  medplum.invalidateSearches("QuestionnaireResponse");
  medplum.invalidateSearches("Observation");
  medplum.invalidateSearches("List");
  medplum.invalidateSearches("Condition");
};

const generateGTPALCloseOutQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
  answers: PregnancyCloseoutFormValues,
  identifier: string,
): Promise<QuestionnaireResponse> => {
  let newQuestionnaireResponse: QuestionnaireResponse;
  const { id: practitionerId } = medplum.getProfile() as Practitioner;

  if (!practitionerId) {
    throw new Error("Practitioner not found");
  }
  const { id: questionnaireId } = await fetchGTPALQuestionnaire(medplum);

  if (!questionnaireId) {
    throw new Error("Questionnaire not found");
  }
  const oldQuestionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
    subject: joinReferenceString("Patient", clientId),
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    _sort: "-_lastUpdated",
  });

  if (oldQuestionnaireResponse) {
    const oldItems = oldQuestionnaireResponse.item ?? [];

    const updatedItems = updateQuestionnaireResponseItems(oldItems, answers, identifier);

    newQuestionnaireResponse = {
      ...oldQuestionnaireResponse,
      status: "in-progress",
      authored: formatISO(new Date()),
      author: { reference: joinReferenceString("Practitioner", practitionerId) },
      item: updatedItems,
      meta: undefined,
    };
  } else {
    const newPregnancyDetailsFormset = createPregnancyDetailsFormset(
      answers,
      1,
      undefined,
      identifier,
    );

    newQuestionnaireResponse = {
      resourceType: "QuestionnaireResponse",
      status: "in-progress",
      authored: formatISO(new Date()),
      author: { reference: joinReferenceString("Practitioner", practitionerId) },
      subject: { reference: joinReferenceString("Patient", clientId) },
      questionnaire: joinReferenceString("Questionnaire", questionnaireId),
      item: [
        {
          linkId: "gtpalGroup",
          text: "GTPAL",
          item: [newPregnancyDetailsFormset],
        },
      ],
      meta: undefined,
    };
  }

  return newQuestionnaireResponse;
};

const generateCloseOutQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
): Promise<void> => {
  const practiceId = localStorage.getItem(ORGANIZATION_ID);
  const { id: practitionerId } = medplum.getProfile() as Practitioner;

  if (!practitionerId) {
    throw new Error("Practitioner not found");
  }

  if (!practiceId) {
    throw new Error("Practice not found");
  }

  const intakeQuestionnaire = await fetchIntakeQuestionnaire(medplum, practiceId);

  if (!intakeQuestionnaire?.id) {
    throw new Error("Intake questionnaire not found");
  }
  const questionnaireId = intakeQuestionnaire.id;

  const intakeQuestionnaireResponse = await medplum.searchOne("QuestionnaireResponse", {
    subject: joinReferenceString("Patient", clientId),
    questionnaire: joinReferenceString("Questionnaire", questionnaireId),
    _sort: "-_lastUpdated",
  });

  const eddQuestionnaire = await fetchEDDQuestionnaire(medplum);
  const activePregnancyList = await fetchActivePregnancyList(medplum, clientId);
  const observationEddGroup = await buildObservationEddGroupItem(medplum, activePregnancyList);

  const currentPregnancyItems = intakeQuestionnaireResponse
    ? getQuestionnaireResponseItems("currentPregnancy", intakeQuestionnaireResponse.item)
    : [];
  const eddQuestionnaireResponse = await fetchEDDQuestionnaireResponse(
    medplum,
    eddQuestionnaire,
    clientId,
  );

  const closeoutItems = mergeCloseoutItemsWithObservationEdd(
    [...(currentPregnancyItems ?? []), ...(eddQuestionnaireResponse?.item ?? [])],
    observationEddGroup,
  );

  const shouldCreateCloseoutQuestionnaire =
    Boolean(intakeQuestionnaireResponse) || Boolean(observationEddGroup);

  if (!shouldCreateCloseoutQuestionnaire) {
    return;
  }

  let extension = undefined as QuestionnaireResponse["extension"];
  if (activePregnancyList) {
    extension = setExtensionValue([], Extensions.PregnancyCloseoutPregnancyRecord, {
      valueReference: createReference(activePregnancyList),
    });
  }

  await medplum.createResource({
    resourceType: "QuestionnaireResponse",
    identifier: {
      system: Systems.QH,
      value: Questionnaires.PregnancyCloseout,
    },
    subject: { reference: joinReferenceString("Patient", clientId) },
    status: "in-progress",
    authored: formatISO(new Date()),
    author: { reference: joinReferenceString("Practitioner", practitionerId) },
    item: closeoutItems,
    extension,
    meta: undefined,
  });

  if (intakeQuestionnaireResponse) {
    const intakeItemsWithoutCurrentPregnancy = removeQuestionnaireResponseItems(
      intakeQuestionnaireResponse.item,
      "currentPregnancy",
    );

    await medplum.updateResource({
      ...intakeQuestionnaireResponse,
      item: [...intakeItemsWithoutCurrentPregnancy],
    });
  }
};

/**
 * Loads the patient's active pregnancy List before close-out cancels dating Observations.
 *
 * @param medplum - Medplum client
 * @param clientId - Patient id
 */
async function fetchActivePregnancyList(
  medplum: MedplumClient,
  clientId: string,
): Promise<List | undefined> {
  const lists = await fetchPregnancyRecordList(medplum, joinReferenceString("Patient", clientId), {
    status: "current",
  });
  return lists?.[0];
}

/**
 * Builds an eddGroup item from LMP/EDD Observations on the active pregnancy record.
 *
 * @param medplum - Medplum client
 * @param activePregnancyList - Active pregnancy List, when present
 */
async function buildObservationEddGroupItem(
  medplum: MedplumClient,
  activePregnancyList: List | undefined,
): Promise<QuestionnaireResponseItem | undefined> {
  if (!activePregnancyList) return undefined;

  const pregnancyRecord = await buildPregnancyRecord(medplum, activePregnancyList);
  if (!pregnancyRecord) return undefined;

  return buildEddGroupItemFromDatingSummary({
    lastMenstrualPeriod: pregnancyRecord.lastMenstrualPeriod,
    edd: pregnancyRecord.edd,
  });
}

/**
 * Recursively removes items with the specified linkId from questionnaire response items
 */
function removeQuestionnaireResponseItems(
  items: QuestionnaireResponseItem[] | undefined,
  linkIdToRemove: string,
): QuestionnaireResponseItem[] {
  if (!items) return [];

  return items
    .filter((item) => item.linkId !== linkIdToRemove)
    .map((item) => {
      if (item.item) {
        return {
          ...item,
          item: removeQuestionnaireResponseItems(item.item, linkIdToRemove),
        };
      }
      return item;
    });
}

/**
 * Creates a new pregnancyDetailsFormset with closeout answers
 */
function createPregnancyDetailsFormset(
  answers: PregnancyCloseoutFormValues,
  code: number,
  templateFormset?: QuestionnaireResponseItem,
  identifier?: string,
): QuestionnaireResponseItem {
  const newFormsetItems: QuestionnaireResponseItem[] = [
    {
      linkId: "pregnancyDetailsFormsetIdentifier",
      extension: [
        {
          url: "http://hl7.org/fhir/StructureDefinition/questionnaire-hidden",
          valueBoolean: true,
        },
      ],
      answer: [{ valueString: identifier ?? "" }],
    },
    {
      linkId: "pregnancyDetailsFormsetCode",
      extension: [
        {
          url: "http://hl7.org/fhir/StructureDefinition/questionnaire-hidden",
          valueBoolean: true,
        },
      ],
      answer: [{ valueString: String(code) }],
    },
  ];

  if (answers.closeoutDate) {
    newFormsetItems.push({
      linkId: "pregnancyDetailsFormsetDate",
      text: "Date",
      extension: [
        {
          url: "http://quiltedhealth.com/sd/questionnaire-field-placeholder",
          valueString: "MM/DD/YYYY",
        },
        {
          url: "http://quiltedhealth.com/sd/questionnaire-field-icon",
          valueString: "calendar",
        },
      ],
      answer: [{ valueDate: format(answers.closeoutDate, "yyyy-MM-dd") }],
    });
  }

  if (answers.outcome?.code && answers.outcome?.system && answers.outcome?.display) {
    newFormsetItems.push({
      linkId: "pregnancyDetailsFormsetOutcome",
      text: "Outcome",
      answer: [
        {
          valueCoding: {
            system: answers.outcome.system,
            code: answers.outcome.code,
            display: answers.outcome.display,
          },
        },
      ],
    });
  }

  if (answers.gestationalAge !== undefined) {
    newFormsetItems.push({
      linkId: "pregnancyDetailsFormsetGestationalAge",
      text: "Gestational Age (GA)",
      extension: [
        {
          url: "http://quiltedhealth.com/sd/questionnaire-item-allow-negative",
          valueBoolean: false,
        },
        {
          url: "http://quiltedhealth.com/sd/questionnaire-field-placeholder",
          valueString: "Weeks pregnant",
        },
      ],
      answer: [{ valueDecimal: answers.gestationalAge }],
    });
  }

  if (answers.notes !== undefined) {
    newFormsetItems.push({
      linkId: "pregnancyDetailsFormsetNotes",
      text: "Are there any additional facts about this pregnancy we should know?",
      extension: [
        {
          url: "http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl",
          valueCodeableConcept: {
            coding: [
              {
                system: "http://hl7.org/fhir/questionnaire-item-control",
                code: "text-box",
                display: "Text Box",
              },
            ],
          },
        },
      ],
      answer: answers.notes ? [{ valueString: answers.notes }] : [],
    });
  }

  return {
    linkId: "pregnancyDetailsFormset",
    text: "Pregnancy Details",
    extension: templateFormset?.extension || [
      {
        url: "http://quiltedhealth.com/sd/questionnaire-item-formset",
        valueString: "Add Another Pregnancy",
      },
      {
        url: "http://quiltedhealth.com/sd/questionnaire-field-group-name-title",
        valueString: "Pregnancy",
      },
      {
        url: "http://quiltedhealth.com/sd/questionnaire-item-formset-show-title",
        valueBoolean: true,
      },
    ],
    item: [...newFormsetItems, { linkId: "pregnancyCloseout", answer: [{ valueBoolean: true }] }],
  };
}

/**
 * Updates questionnaire response items by adding a new pregnancyDetailsFormset
 * with closeout answers inside the gtpalGroup structure
 * Preserves all other existing items and their nested structure
 */
function updateQuestionnaireResponseItems(
  items: QuestionnaireResponseItem[],
  answers: PregnancyCloseoutFormValues,
  identifier?: string,
): QuestionnaireResponseItem[] {
  return items.map((item) => {
    // Find and update the gtpalGroup item
    if (item.linkId === "gtpalGroup" && item.item) {
      const existingFormsetItems = item.item.filter(
        (childItem) => childItem.linkId === "pregnancyDetailsFormset",
      );

      const existingCodes = existingFormsetItems
        .map((formsetItem) => {
          const codeItem = formsetItem.item?.find(
            (subItem) => subItem.linkId === "pregnancyDetailsFormsetCode",
          );
          const codeValue = codeItem?.answer?.[0]?.valueString;
          return codeValue ? parseInt(codeValue, 10) : 0;
        })
        .filter((code) => !isNaN(code));

      const nextCode = existingCodes.length > 0 ? Math.max(...existingCodes) + 1 : 1;

      const templateFormset = existingFormsetItems[0];
      const newPregnancyDetailsFormset = createPregnancyDetailsFormset(
        answers,
        nextCode,
        templateFormset,
        identifier,
      );

      return {
        ...item,
        item: [...item.item, newPregnancyDetailsFormset],
      };
    }

    if (item.item && item.item.length > 0) {
      return {
        ...item,
        item: updateQuestionnaireResponseItems(item.item, answers),
      };
    }

    return item;
  });
}
